import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  StatusStepper,
  type StepperStep,
} from "@/design-system/components/ui/StatusStepper";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { extractErrorMessage } from "@/api/client";
import { emailFieldError } from "@/lib/emailValidator";
import {
  portalAccountsApi,
  type PortalContactSearchResultDto,
  type SendSignupLinkPayload,
  type SendSignupLinkResult,
} from "@/api/portalAccounts";
import { collectionBundlesApi } from "@/api/collectionBundles";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import type { MaterialAudience } from "@/api/portalMaterials";
import { Step1Contact } from "@/components/signup-wizard/Step1Contact";
import { Step2Bundles } from "@/components/signup-wizard/Step2Bundles";
import { Step3Assignees } from "@/components/signup-wizard/Step3Assignees";
import {
  Step4Review,
  type Step4ReviewAssignee,
} from "@/components/signup-wizard/Step4Review";

// External Intake & Collection — Remediation Tier 2 #9 (Prompt 9-ii).
// WS2 — staff full-page wizard that drives
// /api/portal/admin/accounts/signup-link. The wizard preserves the two
// pre-existing modes:
//   (1) brand-new account — pick type + name + email + bundles → the
//       backend mints + emails a magic-link, returning the raw token so
//       staff can copy it in non-prod.
//   (2) standing account — pre-filled email (via ?email=&accountType=,
//       which lock the Step 1 fields); the backend just pushes the new
//       bundles onto the existing dashboard, no fresh invitation. Kept as a
//       supported deep-link contract even though no in-app link currently
//       seeds it (the detail page now routes "Send another request" to the
//       package composer).

const STEPS: StepperStep[] = [
  { key: "who", label: "Who" },
  { key: "what", label: "What they'll work on" },
  { key: "team", label: "Who's working it" },
  { key: "review", label: "Review & send" },
];

type StepIndex = 0 | 1 | 2 | 3;

export function PortalSignupLinkPage() {
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Portal Accounts", href: "/admin/portal-accounts" },
    { label: "Send signup link" },
  ]);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { dateTime } = useDateFormatter();
  const lockedEmail = params.get("email") ?? "";
  const lockedAccountType =
    (params.get("accountType") as MaterialAudience | null) ?? null;
  const lockedName = params.get("fullName") ?? "";

  const [stepIndex, setStepIndex] = useState<StepIndex>(0);
  const [email, setEmail] = useState(lockedEmail);
  const [fullName, setFullName] = useState(lockedName);
  // PROB-03 — no implicit "Buyer" default; staff must pick the party
  // type before the form submits. The pre-locked variant from
  // ?accountType= still seeds it.
  const [accountType, setAccountType] = useState<MaterialAudience | "">(
    lockedAccountType ?? "",
  );
  const [selectedBundles, setSelectedBundles] = useState<string[]>([]);
  const [result, setResult] = useState<SendSignupLinkResult | null>(null);
  // Block-on-existing — when the picked or typed email already has an
  // ACTIVE portal account (!IsDisabled && !IsArchived). Stamped from the
  // typeahead select or from the on-blur lookup; cleared on email edit.
  // Holds just the account id so we can link to /admin/portal-accounts/{id}
  // where "add more bundles" already lives.
  const [existingAccountError, setExistingAccountError] = useState<
    { id: string } | null
  >(null);
  // PROB-I — the sending staff member is auto-assigned on the backend; this
  // picker lets them add teammates up front. Optional auth so the page still
  // renders in tests that don't bootstrap an AuthProvider.
  const me = useOptionalAuth()?.user ?? null;
  const [teammateIds, setTeammateIds] = useState<string[]>([]);

  // Bundle picker is filtered to the chosen audience plus untagged
  // bundles (audience = null reads as "any audience").
  const bundles = useQuery({
    queryKey: ["collection-bundles", accountType],
    queryFn: () => collectionBundlesApi.list(false, null),
  });

  const audienceMatch = useMemo(() => {
    const rows = bundles.data ?? [];
    return rows.filter(
      (b) => b.audience === null || b.audience === accountType,
    );
  }, [bundles.data, accountType]);

  const selectedBundleDtos = useMemo(
    () => audienceMatch.filter((b) => selectedBundles.includes(b.id)),
    [audienceMatch, selectedBundles],
  );

  // Step 4 review-screen assignee summary: include the sender chip and
  // resolved teammate display names (the teammate picker stores just ids,
  // so resolved display lives on the picker's internal cache — we surface
  // the ids here and let the page renderer fall back to a stable label).
  // Hoisted above the early-return so hook order stays stable.
  const reviewAssignees: Step4ReviewAssignee[] = useMemo(() => {
    const rows: Step4ReviewAssignee[] = [];
    if (me) {
      rows.push({ id: me.id, displayName: me.displayName, isSender: true });
    }
    for (const id of teammateIds) {
      rows.push({ id, displayName: `Teammate ${id.slice(0, 6)}`, isSender: false });
    }
    return rows;
  }, [me, teammateIds]);

  const send = useMutation({
    mutationFn: (body: SendSignupLinkPayload) =>
      portalAccountsApi.sendSignupLink(body),
    onSuccess: (r) => setResult(r),
  });

  // Inline email-format check mirrors the Leads / Brokers forms: server
  // validation is authoritative, but staff see a malformed address before
  // submitting. Emptiness is gated by canSubmit, so we don't nag on an
  // untouched field — only a present-but-malformed value shows an error.
  const emailError = emailFieldError(email, { required: false });

  // Step 1 valid = enough to keep moving in the wizard.
  const step1Valid =
    !!email.trim() &&
    !emailError &&
    !!fullName.trim() &&
    !!accountType &&
    !existingAccountError;

  // FL-5: assignment groups are no longer hard-required — staff can stand
  // up an account first and provision bundles afterward.
  // PROB-03 — accountType is now required (no implicit Buyer default).
  // Block-on-existing — !existingAccountError keeps submit disabled when
  // the typed/picked email already belongs to an active portal account.
  const canSubmit =
    !!email.trim() &&
    !!fullName.trim() &&
    !!accountType &&
    !emailError &&
    !existingAccountError &&
    !send.isPending;

  const handleEmailChange = (next: string) => {
    setEmail(next);
    // Clear the stale block as soon as staff starts editing the email —
    // the on-blur lookup will re-set it if the new value still collides.
    if (existingAccountError) setExistingAccountError(null);
  };

  const handleSelectContact = (r: PortalContactSearchResultDto) => {
    setEmail(r.email);
    setFullName(r.displayName);
    // Only prefill party type when the source is unambiguous (Broker/Vendor).
    // Person/LegalEntity/Recipient leave it untouched so staff picks the role.
    if (!lockedAccountType && r.suggestedAccountType) {
      setAccountType(r.suggestedAccountType as MaterialAudience);
    }
    if (r.portalAccount?.isActive) {
      setExistingAccountError({ id: r.portalAccount.id });
    } else {
      setExistingAccountError(null);
    }
  };

  const handleEmailBlur = async (typed: string) => {
    // Skip the lookup in the deep-link / locked-email mode — the parent
    // surface is the "add more bundles" path, not a duplicate-account guard.
    if (lockedEmail) return;
    const normalized = typed.trim().toLowerCase();
    if (normalized.length < 3) {
      // Not a plausible email yet; don't burn a search request.
      return;
    }
    try {
      const rows = await portalAccountsApi.searchContacts(normalized);
      const hit = rows.find(
        (r) => r.email.toLowerCase() === normalized && r.portalAccount?.isActive,
      );
      if (hit && hit.portalAccount) {
        setExistingAccountError({ id: hit.portalAccount.id });
      } else if (existingAccountError) {
        // Earlier-picked active account no longer matches what staff typed.
        setExistingAccountError(null);
      }
    } catch {
      // Silent — the form-level send.error already surfaces real submit
      // failures; a backgrounded lookup miss shouldn't gate the page.
    }
  };

  const handleToggleBundle = (id: string) => {
    setSelectedBundles((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSend = () => {
    if (!accountType || emailError || existingAccountError) return;
    send.mutate({
      email: email.trim(),
      fullName: fullName.trim(),
      accountType,
      bundleIds: selectedBundles,
      additionalAssigneeUserIds: teammateIds,
    });
  };

  // Confirmation screen — unchanged surface from the pre-wizard page.
  if (result) {
    return (
      <div className="flex flex-col gap-space-4">
        <PageHeader
          eyebrow="Portal Accounts"
          title={
            result.isExistingAccount
              ? "Assignments added"
              : "Signup link sent"
          }
          description={
            result.isExistingAccount
              ? `${result.packageIds.length} new request(s) added to the standing ${accountType} account. The party will see them on their dashboard at next sign-in.`
              : `${result.packageIds.length} request(s) created. A set-password email has been sent to ${email}.`
          }
        />
        {/* PROB-I — confirm the account landed assigned, not orphaned. */}
        {result.assigneeUserIds.length > 0 && (
          <p className="text-small text-onyx m-0">
            Assigned to {result.assigneeUserIds.length} staff member
            {result.assigneeUserIds.length === 1 ? "" : "s"} (including you).
          </p>
        )}
        {!result.isExistingAccount && result.invitation && (
          <div className="p-space-3 border border-gold/40 rounded-md bg-gold/10">
            <p className="text-small text-onyx m-0">Set-password token (non-prod test seam):</p>
            <code className="block mt-space-2 text-caption break-all">
              {result.invitation.token}
            </code>
            <p className="text-caption text-gray-mid mt-space-2 m-0">
              Expires {dateTime(result.invitation.expiresAtUtc)}.
              In production, the token is delivered by email only.
            </p>
          </div>
        )}
        <div className="flex gap-space-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setResult(null);
              setSelectedBundles([]);
              setStepIndex(0);
            }}
          >
            Send another
          </Button>
          <Button
            type="button"
            onClick={() => navigate(`/admin/portal-accounts/${result.portalUserId}`)}
          >
            View account
          </Button>
        </div>
      </div>
    );
  }

  // Validation on Step 1 also doubles as "advance allowed". Steps 2 and 3
  // are optional, so Next is unconditionally enabled there.
  const canAdvance = stepIndex === 0 ? step1Valid : true;

  const goNext = () => {
    if (stepIndex < 3) setStepIndex((stepIndex + 1) as StepIndex);
  };
  const goBack = () => {
    if (stepIndex > 0) setStepIndex((stepIndex - 1) as StepIndex);
  };

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader eyebrow="Portal Accounts" title="Send signup link" />

      <StatusStepper
        steps={STEPS}
        currentIndex={stepIndex}
        complete={false}
        label="Signup link progress"
        data-testid="signup-wizard-stepper"
      />

      <div className="flex flex-col gap-space-4" data-testid="signup-wizard-body">
        {stepIndex === 0 && (
          <Step1Contact
            email={email}
            fullName={fullName}
            accountType={accountType}
            emailError={emailError}
            lockedEmail={lockedEmail}
            lockedAccountType={lockedAccountType}
            existingAccountId={existingAccountError?.id ?? null}
            onEmailChange={handleEmailChange}
            onFullNameChange={setFullName}
            onAccountTypeChange={setAccountType}
            onSelectContact={handleSelectContact}
            onEmailBlur={handleEmailBlur}
          />
        )}
        {stepIndex === 1 && (
          <Step2Bundles
            bundles={audienceMatch}
            selectedBundleIds={selectedBundles}
            onToggleBundle={handleToggleBundle}
            isLoading={bundles.isLoading}
            error={bundles.error}
          />
        )}
        {stepIndex === 2 && (
          <Step3Assignees
            me={me ? { id: me.id, displayName: me.displayName } : null}
            teammateIds={teammateIds}
            onTeammateIdsChange={setTeammateIds}
          />
        )}
        {stepIndex === 3 && (
          <Step4Review
            email={email}
            fullName={fullName}
            accountType={accountType}
            selectedBundles={selectedBundleDtos}
            assignees={reviewAssignees}
            senderName={me?.displayName ?? ""}
          />
        )}
      </div>

      {send.error && (
        <p className="text-small text-status-error" role="alert">
          {extractErrorMessage(send.error)}
        </p>
      )}

      <div
        className="flex items-center justify-between gap-space-2 pt-space-2 border-t border-border-light"
        data-testid="signup-wizard-footer"
      >
        <Button
          type="button"
          variant="text"
          onClick={() => navigate("/admin/portal-accounts")}
        >
          Cancel
        </Button>
        <div className="flex gap-space-2">
          <Button
            type="button"
            variant="secondary"
            onClick={goBack}
            disabled={stepIndex === 0}
          >
            Back
          </Button>
          {stepIndex < 3 ? (
            <Button
              type="button"
              onClick={goNext}
              disabled={!canAdvance}
            >
              Next
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSend}
              disabled={!canSubmit}
            >
              {send.isPending ? "Sending…" : "Send signup link"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

